import { Stack, Theme } from '@mui/material';
import Decimal from 'decimal.js';
import { set } from 'lodash';
import { ReactNode } from 'react';

import {
  EMPTY_CHART_BACKGROUND_CSS,
  PRIMARY_CANDY_CANE,
  SECONDARY_CANDY_CANE,
  useChartColorDefinitions,
} from '@/components/charts/constants';
import { LegendItem } from '@/components/charts/Legend/Legend';
import { Section } from '@/components/charts/StackedHorizontalBar/StackedHorizontalBar';
import { PopperContent } from '@/components/poppers/PopperContent';
import { MiniTable } from '@/components/tables/MiniTable/MiniTable';
import { COLORS } from '@/styles/tokens/colors';
import { StateCode } from '@/types/schema';
import {
  formatCurrency,
  formatCurrencyNoDecimals,
} from '@/utils/formatting/currency';

import { ClientDetailsGiftingPage_HouseholdFragment } from '../graphql/ClientDetailsGiftingPage.generated';

export const ZERO_DECIMAL = new Decimal(0);

interface GetGSTExemptionSectionsInput {
  colors: ReturnType<typeof useChartColorDefinitions>;
  clampNegativeValuesToZero: (input: Decimal) => Decimal;
  gstExemptionAmountForAllGrantors?: Decimal;
  possiblePrimaryClients: ClientDetailsGiftingPage_HouseholdFragment['possiblePrimaryClients'];
  gstExclusionUsedTotal: Decimal;
  groupByGrantor: boolean;
  gstExemptionAmountPerGrantor: Decimal | null;
  grantorIdToExemptionsMap: Record<
    string,
    {
      lifetimeExemptionAmount: Decimal;
      gstExemptionAmount: Decimal;
    }
  >;
}

export function getGSTExemptionSections({
  colors,
  clampNegativeValuesToZero,
  gstExemptionAmountForAllGrantors,
  possiblePrimaryClients,
  gstExclusionUsedTotal,
  groupByGrantor,
  gstExemptionAmountPerGrantor,
  grantorIdToExemptionsMap,
}: GetGSTExemptionSectionsInput) {
  const value = clampNegativeValuesToZero(
    gstExemptionAmountForAllGrantors ?? ZERO_DECIMAL
  );

  const sectionsByGrantor: Section[] = [];
  const sectionForAllGrantors: Section[] = [
    {
      value: value.toNumber(),
      legendPrimaryText: formatCurrency(value, {
        notation: 'compact',
        currencySign: 'accounting',
      }),
      color: getColorForIndex(0, colors),
      label: 'Total remaining GST exemption',
      tooltip: (
        <PopperContent
          sx={{
            width: 'auto',
          }}
          body={
            <MiniTable
              variant="default"
              rows={possiblePrimaryClients.map((client) => {
                // this value is how much GST exemption is left for this grantor
                const lifetimeGSTExemptionLeftForGrantor =
                  gstExemptionAmountPerGrantor?.minus(
                    client.gstExclusionUsed ?? 0
                  ) ?? ZERO_DECIMAL;

                return {
                  label: client.displayName,
                  value: formatCurrencyNoDecimals(
                    clampNegativeValuesToZero(
                      lifetimeGSTExemptionLeftForGrantor
                    )
                  ),
                };
              })}
            />
          }
        />
      ),
    },
  ];

  possiblePrimaryClients.forEach((client, i) => {
    const color = getColorForIndex(i, colors);

    // this value is how much GST exemption is left for this grantor
    const lifetimeGSTExemptionLeftForGrantor =
      gstExemptionAmountPerGrantor?.minus(client.gstExclusionUsed ?? 0) ??
      ZERO_DECIMAL;

    set(
      grantorIdToExemptionsMap,
      `${client.id}.gstExemptionAmount`,
      clampNegativeValuesToZero(lifetimeGSTExemptionLeftForGrantor)
    );

    const value = clampNegativeValuesToZero(lifetimeGSTExemptionLeftForGrantor);

    sectionsByGrantor.push({
      value: value.toNumber(),
      legendPrimaryText: formatCurrency(value, {
        notation: 'compact',
        currencySign: 'accounting',
      }),
      color,
      label: client.displayName,
      tooltip: (
        <PopperContent
          sx={{
            width: 'auto',
          }}
          body={
            <MiniTable
              variant="default"
              rows={[
                {
                  label: client.displayName,
                  value: formatCurrencyNoDecimals(
                    clampNegativeValuesToZero(
                      lifetimeGSTExemptionLeftForGrantor
                    )
                  ),
                },
              ]}
            />
          }
        />
      ),
    });
  });

  return [
    ...(groupByGrantor ? sectionsByGrantor : sectionForAllGrantors),
    // include one section that shows the used exemption
    {
      value: clampNegativeValuesToZero(gstExclusionUsedTotal).toNumber(),
      legendPrimaryText: formatCurrency(
        clampNegativeValuesToZero(gstExclusionUsedTotal),
        {
          notation: 'compact',
          currencySign: 'accounting',
        }
      ),
      color: COLORS.GRAY[200],
      backgroundCss: EMPTY_CHART_BACKGROUND_CSS,
      omitFromLegend: true,
      tooltip: (
        <PopperContent
          body={`${formatCurrency(
            clampNegativeValuesToZero(gstExclusionUsedTotal),
            {
              notation: 'compact',
            }
          )} used`}
        />
      ),
    },
  ];
}

interface GetCTExemptionSectionsInput {
  colors: ReturnType<typeof useChartColorDefinitions>;
  clampNegativeValuesToZero: (input: Decimal) => Decimal;
  possiblePrimaryClients: ClientDetailsGiftingPage_HouseholdFragment['possiblePrimaryClients'];
  ctExemptionTotalAmount: Decimal;
  ctExemptionUsedTotal: Decimal;
  groupByGrantor: boolean;
}

export function getCTExemptionSections({
  colors,
  clampNegativeValuesToZero,
  possiblePrimaryClients,
  ctExemptionTotalAmount,
  ctExemptionUsedTotal,
  groupByGrantor,
}: GetCTExemptionSectionsInput) {
  const ctGrantors = possiblePrimaryClients.filter(
    (client) => client.address?.stateCode === StateCode.Ct
  );

  if (ctGrantors.length === 0) {
    return [];
  }

  const totalRemainingCTExemption = clampNegativeValuesToZero(
    ctExemptionTotalAmount.minus(ctExemptionUsedTotal)
  );

  // Helper function to calculate CT exemption used for a single grantor
  const getCtExemptionUsedForGrantor = (client: (typeof ctGrantors)[0]) => {
    return client.stateExemptionUsed ?? ZERO_DECIMAL;
  };

  // Helper function to calculate remaining CT exemption for a single grantor
  const getRemainingCtExemptionForGrantor = (
    client: (typeof ctGrantors)[0]
  ) => {
    const used = getCtExemptionUsedForGrantor(client);
    const total = client.totalStateExemption ?? ZERO_DECIMAL;
    return total.minus(used);
  };

  const sectionsByGrantor: Section[] = [];
  const sectionForAllGrantors: Section[] = [
    {
      value: totalRemainingCTExemption.toNumber(),
      legendPrimaryText: formatCurrency(totalRemainingCTExemption, {
        notation: 'compact',
        currencySign: 'accounting',
      }),
      color: getColorForIndex(0, colors),
      label: 'Total remaining CT exemption',
      tooltip: (
        <PopperContent
          sx={{
            width: 'auto',
          }}
          body={
            <MiniTable
              variant="default"
              rows={ctGrantors.map((client) => ({
                label: client.displayName,
                value: formatCurrencyNoDecimals(
                  clampNegativeValuesToZero(
                    getRemainingCtExemptionForGrantor(client)
                  )
                ),
              }))}
            />
          }
        />
      ),
    },
  ];

  ctGrantors.forEach((client, i) => {
    const color = getColorForIndex(i, colors);
    const value = clampNegativeValuesToZero(
      getRemainingCtExemptionForGrantor(client)
    );

    sectionsByGrantor.push({
      value: value.toNumber(),
      legendPrimaryText: formatCurrency(value, {
        notation: 'compact',
        currencySign: 'accounting',
      }),
      color,
      label: client.displayName,
      tooltip: (
        <PopperContent
          sx={{
            width: 'auto',
          }}
          body={
            <MiniTable
              variant="default"
              rows={[
                {
                  label: client.displayName,
                  value: formatCurrencyNoDecimals(value),
                },
              ]}
            />
          }
        />
      ),
    });
  });

  return [
    ...(groupByGrantor ? sectionsByGrantor : sectionForAllGrantors),
    // include one section that shows the used exemption
    {
      value: clampNegativeValuesToZero(ctExemptionUsedTotal).toNumber(),
      legendPrimaryText: formatCurrency(
        clampNegativeValuesToZero(ctExemptionUsedTotal),
        {
          notation: 'compact',
          currencySign: 'accounting',
        }
      ),
      color: COLORS.GRAY[200],
      backgroundCss: EMPTY_CHART_BACKGROUND_CSS,
      omitFromLegend: true,
      tooltip: (
        <PopperContent
          body={`${formatCurrency(
            clampNegativeValuesToZero(ctExemptionUsedTotal),
            {
              notation: 'compact',
            }
          )} used`}
        />
      ),
    },
  ];
}

interface GetLifetimeExemptionSectionsInput {
  colors: ReturnType<typeof useChartColorDefinitions>;
  clampNegativeValuesToZero: (input: Decimal) => Decimal;
  lifetimeExemptionAmountForAllGrantors?: Decimal;
  possiblePrimaryClients: ClientDetailsGiftingPage_HouseholdFragment['possiblePrimaryClients'];
  lifetimeExclusionUsedTotal: Decimal;
  groupByGrantor: boolean;
  lifetimeExemptionAmountPerGrantor: Decimal | null;
  grantorIdToExemptionsMap: Record<
    string,
    {
      lifetimeExemptionAmount: Decimal;
      gstExemptionAmount: Decimal;
    }
  >;
  theme: Theme;
}

export function getLifetimeExemptionSections({
  colors,
  clampNegativeValuesToZero,
  lifetimeExemptionAmountForAllGrantors,
  possiblePrimaryClients,
  lifetimeExclusionUsedTotal,
  groupByGrantor,
  lifetimeExemptionAmountPerGrantor,
  grantorIdToExemptionsMap,
  theme,
}: GetLifetimeExemptionSectionsInput) {
  const value = clampNegativeValuesToZero(
    lifetimeExemptionAmountForAllGrantors ?? ZERO_DECIMAL
  );

  const sectionsByGrantor: (Section & {
    clientId: string;
  })[] = [];
  const dsueByGrantor: (Section & {
    clientId: string;
    legendIndex?: number;
    omitFromLegend?: boolean;
    LegendItem?: ReactNode;
  })[] = [];

  // Total remaining lifetime exemption (separate from DSUE)
  const sectionForAllGrantors: Section[] = [
    {
      value: value.toNumber(),
      legendPrimaryText: formatCurrency(value, {
        notation: 'compact',
        currencySign: 'accounting',
      }),
      color: getColorForIndex(0, colors),
      label: 'Total remaining lifetime exemption',
      tooltip: (
        <PopperContent
          sx={{
            width: 'auto',
          }}
          body={
            <MiniTable
              variant="default"
              rows={possiblePrimaryClients.map((client) => {
                // this value is how much lifetime exemption is left for this grantor
                const lifetimeExemptionLeftForGrantor =
                  lifetimeExemptionAmountPerGrantor?.minus(
                    client.lifetimeExclusionUsed ?? 0
                  ) ?? ZERO_DECIMAL;

                return {
                  label: client.displayName,
                  value: formatCurrencyNoDecimals(
                    clampNegativeValuesToZero(lifetimeExemptionLeftForGrantor)
                  ),
                };
              })}
            />
          }
        />
      ),
    },
  ];

  // Calculate total DSUE amount for all clients
  let totalDsueAmount = ZERO_DECIMAL;

  // Track this because we only want to add one DSUE legend item
  let hasAddedDsueSection = false;

  possiblePrimaryClients.forEach((client, i) => {
    const color = getColorForIndex(i, colors);
    const backgroundCss =
      i % 2 === 0 ? PRIMARY_CANDY_CANE(theme) : SECONDARY_CANDY_CANE(theme);

    // this value is how much lifetime exemption is left for this grantor
    const lifetimeExemptionLeftForGrantor =
      lifetimeExemptionAmountPerGrantor?.minus(
        client.lifetimeExclusionUsed ?? 0
      ) ?? ZERO_DECIMAL;

    const value = clampNegativeValuesToZero(lifetimeExemptionLeftForGrantor);

    set(
      grantorIdToExemptionsMap,
      `${client.id}.lifetimeExemptionAmount`,
      clampNegativeValuesToZero(lifetimeExemptionLeftForGrantor)
    );

    // Add regular exemption section for each client
    sectionsByGrantor.push({
      value: value.toNumber(),
      clientId: client.id,
      legendPrimaryText: formatCurrency(value, {
        notation: 'compact',
        currencySign: 'accounting',
      }),
      color,
      label: client.displayName,
      tooltip: (
        <PopperContent
          sx={{
            width: 'auto',
          }}
          body={
            <MiniTable
              variant="default"
              rows={[
                {
                  label: client.displayName,
                  value: formatCurrencyNoDecimals(
                    clampNegativeValuesToZero(lifetimeExemptionLeftForGrantor)
                  ),
                },
              ]}
            />
          }
        />
      ),
    });

    // Add DSUE section if the client has DSUE
    if (client.dsue?.amount.gt(0)) {
      const dsueAmount = new Decimal(client.dsue.amount);
      totalDsueAmount = totalDsueAmount.plus(dsueAmount);

      const LegendItemNode = !hasAddedDsueSection ? (
        <Stack
          direction="row"
          spacing={0.25}
          height="fit-content"
          alignItems="center"
        >
          <LegendItem
            primaryText=""
            backgroundCss={PRIMARY_CANDY_CANE(theme)}
            legendItemColor={COLORS.PRIMITIVES.WHITE}
          />
          <LegendItem
            primaryText="DSUE"
            backgroundCss={SECONDARY_CANDY_CANE(theme)}
            legendItemColor={COLORS.PRIMITIVES.BLACK}
          />
        </Stack>
      ) : null;

      hasAddedDsueSection = true;

      dsueByGrantor.push({
        value: dsueAmount.toNumber(),
        clientId: client.id,
        omitFromLegend: !LegendItemNode,
        LegendItem: LegendItemNode,
        backgroundCss,
        color: COLORS.PRIMITIVES.WHITE,
        legendIndex: Infinity,
        tooltip: (
          <PopperContent
            sx={{
              width: 'auto',
            }}
            body={
              <MiniTable
                variant="default"
                rows={[
                  {
                    label: `${client.displayName} DSUE`,
                    value: formatCurrencyNoDecimals(dsueAmount),
                  },
                ]}
              />
            }
          />
        ),
      });
    }
  });

  // Add DSUE section for all clients combined if grouped by grantor
  const sectionForAllDsue = totalDsueAmount.greaterThan(0)
    ? [
        {
          value: totalDsueAmount.toNumber(),
          legendIndex: Infinity,
          legendPrimaryText: 'DSUE',
          backgroundCss: PRIMARY_CANDY_CANE(theme),
          color: COLORS.PRIMITIVES.WHITE,
          tooltip: (
            <PopperContent
              sx={{
                width: 'auto',
              }}
              body={
                <MiniTable
                  variant="default"
                  rows={possiblePrimaryClients
                    .filter((client) => client.dsue?.amount)
                    .map((client) => ({
                      label: `${client.displayName} DSUE`,
                      value: formatCurrencyNoDecimals(
                        new Decimal(client.dsue?.amount || 0)
                      ),
                    }))}
                />
              }
            />
          ),
        },
      ]
    : [];

  const sections = (() => {
    const sections = [];
    if (!groupByGrantor) {
      sections.push(...sectionForAllGrantors);
      if (sectionForAllDsue.length > 0) {
        sections.push(...sectionForAllDsue);
      }
    }

    if (groupByGrantor) {
      sectionsByGrantor.forEach((section) => {
        const clientId = section.clientId;
        const dsue = dsueByGrantor.find((dsue) => dsue.clientId === clientId);

        if (dsue) {
          sections.push(section);
          sections.push(dsue);
        } else {
          sections.push(section);
        }
      });
    } else {
      sections.push(...sectionForAllGrantors);
    }

    return sections;
  })();

  return [
    ...sections,
    // include one section that shows the used exemption
    {
      value: clampNegativeValuesToZero(lifetimeExclusionUsedTotal).toNumber(),
      legendPrimaryText: formatCurrency(
        clampNegativeValuesToZero(lifetimeExclusionUsedTotal),
        {
          notation: 'compact',
          currencySign: 'accounting',
        }
      ),
      color: COLORS.GRAY[200],
      backgroundCss: EMPTY_CHART_BACKGROUND_CSS,
      omitFromLegend: true,
      tooltip: (
        <PopperContent
          body={`${formatCurrency(
            clampNegativeValuesToZero(lifetimeExclusionUsedTotal),
            {
              notation: 'compact',
            }
          )} used`}
        />
      ),
    },
  ];
}

export function getColorForIndex(
  index: number,
  colors: ReturnType<typeof useChartColorDefinitions>
): string {
  // alternate between primary and secondary colors
  const colorsToPickFrom = [
    colors.PRIMARY.backgroundColor,
    colors.SECONDARY.backgroundColor,
  ];

  return colorsToPickFrom[index % colorsToPickFrom.length]!;
}

export function createClampNegativeValuesToZero(returnNegativeValues: boolean) {
  // all of these functions are to bind Decimal/Decimal | undefined types to their outputs,
  // as all cases share the same logic but there are cases where the input is Decimal (and
  // expects Decimal output) or Decimal | undefined (and doesn't care about the output)
  function clampNegativeValuesToZero(input: Decimal): Decimal;
  function clampNegativeValuesToZero(
    input: Decimal | undefined
  ): Decimal | undefined;
  function clampNegativeValuesToZero(input: Decimal | undefined) {
    if (returnNegativeValues || !Decimal.isDecimal(input)) {
      return input;
    }
    return input.lessThanOrEqualTo(ZERO_DECIMAL) ? ZERO_DECIMAL : input;
  }
  return clampNegativeValuesToZero;
}
